import { TFunction } from 'i18next';
import { expression_eval } from 'utils/expression';

// Pure expression-editor logic. Stored expressions use the runtime [kpiId] grammar. The editor
// may display names only when converting id -> name -> id reproduces the stored text exactly.

export type ExpressionValidationState = 'pending' | 'valid' | 'invalid';

export interface ExpressionReference {
  id: number;
  name: string;
  isNumeric: boolean;
  isSelectable: boolean;
}

const REF_RE = /\[([^\]]*)\]/g;
const ID_REF_RE = /\[(\d+)\]/g;
// SUBTASK_MULITPLY is misspelled to match the stored runtime grammar
// (globalRegExKPIExpressionFuncSubTaskMultiply in globalConstants.ts) and must not be corrected.
const SUBTASK_RE = /SUBTASK_SUM\[[^\]]*\]|SUBTASK_MULITPLY\[[^\]]*\]/gi;
const MAX_SUGGESTIONS = 8;

const normName = (name: string): string => name.trim().toLowerCase();

const isReferenceableName = (name: string): boolean =>
  name.trim() !== '' && !/[[\]]/.test(name) && !/^\d+$/.test(name.trim());

const referencesByName = (references: ExpressionReference[]): Map<string, ExpressionReference> => {
  const byName = new Map<string, ExpressionReference>();
  const duplicated = new Set<string>();
  for (const reference of references) {
    const key = normName(reference.name);
    if (duplicated.has(key)) continue;
    if (byName.has(key)) {
      byName.delete(key);
      duplicated.add(key);
    } else {
      byName.set(key, reference);
    }
  }

  return byName;
};

const resolveReference = (
  value: string,
  references: ExpressionReference[],
): ExpressionReference | undefined => {
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) {
    const id = Number(trimmed);

    return references.find((reference) => reference.id === id);
  }

  return referencesByName(references).get(normName(trimmed));
};

export const idToName = (expression: string, references: ExpressionReference[]): string => {
  const byId = new Map(references.map((reference) => [reference.id, reference]));

  return expression.replace(ID_REF_RE, (match, id) => {
    const reference = byId.get(Number(id));

    return reference ? `[${reference.name}]` : match;
  });
};

export const nameToId = (expression: string, references: ExpressionReference[]): string => {
  const byName = referencesByName(references);

  return expression.replace(REF_RE, (match, name) => {
    const reference = byName.get(normName(String(name)));

    return reference ? `[${reference.id}]` : match;
  });
};

export const canUseNameMode = (stored: string, references: ExpressionReference[]): boolean => {
  if (references.length === 0) return false;

  // The round-trip itself is the guard. Unrelated duplicate or unusual names must not force the
  // entire editor into id mode; only names actually substituted into this expression matter.
  return nameToId(idToName(stored, references), references) === stored;
};

export const getGrandfatheredReferenceIds = (
  originalStoredValue: string,
  references: ExpressionReference[],
): Set<number> => {
  const byId = new Map(references.map((reference) => [reference.id, reference]));
  const result = new Set<number>();
  for (const match of originalStoredValue.matchAll(new RegExp(ID_REF_RE.source, 'g'))) {
    const id = Number(match[1]);
    const reference = byId.get(id);
    if (reference?.isNumeric && !reference.isSelectable) result.add(id);
  }

  return result;
};

export interface ExpressionSuggestion {
  items: ExpressionReference[];
  from: number;
  to: number;
  query: string;
}

export const computeSuggestion = (
  text: string,
  caret: number,
  references: ExpressionReference[],
): ExpressionSuggestion | undefined => {
  const usable = references.filter((reference) => reference.isSelectable && isReferenceableName(reference.name));
  if (usable.length === 0) return undefined;

  const open = text.lastIndexOf('[', caret - 1);
  const closeAfterOpen = open >= 0 ? text.indexOf(']', open) : -1;
  const insideBracket = open >= 0 && (closeAfterOpen === -1 || closeAfterOpen >= caret);

  let query: string;
  let from: number;
  let to: number;

  if (insideBracket) {
    query = normName(text.slice(open + 1, caret));
    from = open;
    to = caret;
    const close = text.indexOf(']', caret);
    if (close !== -1 && text.lastIndexOf('[', close) === open) to = close + 1;
  } else {
    const run = (text.slice(0, caret).match(/[^+\-*/()[\]]+$/) ?? [''])[0];
    query = normName(run);
    if (query.length < 2) return undefined;
    from = caret - run.trimStart().length;
    to = caret;
  }

  const nameMatches = usable.filter((reference) => normName(reference.name).includes(query));
  const nameMatched = new Set(nameMatches);
  const idMatches =
    query !== '' && /^\d+$/.test(query)
      ? usable.filter((reference) => String(reference.id).startsWith(query) && !nameMatched.has(reference))
      : [];
  const items = [...nameMatches, ...idMatches].slice(0, MAX_SUGGESTIONS);

  return items.length > 0 ? { items, from, to, query } : undefined;
};

export type TokenType = 'field' | 'unknownField' | 'number' | 'operator' | 'paren' | 'text';

export interface Token {
  type: TokenType;
  text: string;
  reference?: ExpressionReference;
}

const TOKENIZE_RE = /\[[^\]]*\]|\d+(?:\.\d+)?|[+\-*/]|[()]|\s+|[^\s+\-*/()[\]]+|[\s\S]/g;

export const tokenizeExpression = (text: string, references?: ExpressionReference[]): Token[] => {
  const tokens: Token[] = [];
  let match: RegExpExecArray | null;
  const re = new RegExp(TOKENIZE_RE.source, 'g');

  while ((match = re.exec(text)) !== null) {
    const value = match[0];
    if (value.startsWith('[') && value.endsWith(']')) {
      const reference = references ? resolveReference(value.slice(1, -1), references) : undefined;
      if (reference) tokens.push({ type: 'field', text: value, reference });
      else if (references) tokens.push({ type: 'unknownField', text: value });
      else tokens.push({ type: 'field', text: value });
    } else if (/^\d/.test(value)) tokens.push({ type: 'number', text: value });
    else if (/^[+\-*/]$/.test(value)) tokens.push({ type: 'operator', text: value });
    else if (value === '(' || value === ')') tokens.push({ type: 'paren', text: value });
    else tokens.push({ type: 'text', text: value });
  }

  return tokens;
};

export type ExpressionError =
  | { kind: 'parens' }
  | { kind: 'emptyReference' }
  | { kind: 'unknownField'; name: string; suggestion?: string }
  | { kind: 'nonNumericField'; name: string }
  | { kind: 'outOfScopeField'; name: string }
  | { kind: 'missingOperator'; left: string; right: string }
  | { kind: 'syntax' };

export const expressionErrorMessage = (t: TFunction<string[]>, error: ExpressionError): string => {
  switch (error.kind) {
    case 'parens':
      return t('kpis:Expression.ErrParens');
    case 'emptyReference':
      return t('kpis:Expression.ErrEmptyReference');
    case 'unknownField':
      return error.suggestion
        ? t('kpis:Expression.ErrUnknownFieldSuggest', { name: error.name, suggestion: error.suggestion })
        : t('kpis:Expression.ErrUnknownField', { name: error.name });
    case 'nonNumericField':
      return t('kpis:Expression.ErrFieldNotNumeric', { name: error.name });
    case 'outOfScopeField':
      return t('kpis:Expression.ErrFieldOutOfScope', { name: error.name });
    case 'missingOperator':
      return t('kpis:Expression.ErrMissingOperator', { left: error.left, right: error.right });
    case 'syntax':
      return t('kpis:Expression.ErrSyntax');
  }
};

const levenshtein = (a: string, b: string): number => {
  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let row = 1; row <= a.length; row++) {
    let diagonal = previous[0];
    previous[0] = row;
    for (let column = 1; column <= b.length; column++) {
      const prior = previous[column];
      previous[column] = Math.min(
        previous[column] + 1,
        previous[column - 1] + 1,
        diagonal + (a[row - 1] === b[column - 1] ? 0 : 1),
      );
      diagonal = prior;
    }
  }

  return previous[b.length];
};

export const closestFieldName = (input: string, names: string[]): string | undefined => {
  const query = input.toLowerCase();
  let best: string | undefined;
  let bestScore = Infinity;

  names.forEach((name) => {
    const score = levenshtein(query, name.toLowerCase());
    if (score < bestScore) {
      best = name;
      bestScore = score;
    }
  });

  return best && bestScore <= Math.max(2, Math.floor(query.length / 2)) ? best : undefined;
};

export interface ExpressionValidationContext {
  references?: ExpressionReference[];
  grandfatheredIds?: Set<number>;
}

export const validateExpression = (
  text: string,
  context: ExpressionValidationContext = {},
): ExpressionError | undefined => {
  let depth = 0;
  for (const character of text) {
    if (character === '(') depth++;
    else if (character === ')') {
      depth--;
      if (depth < 0) return { kind: 'parens' };
    }
  }
  if (depth !== 0) return { kind: 'parens' };

  const rawReferences = [...text.matchAll(new RegExp(REF_RE.source, 'g'))].map((match) => match[1].trim());
  if (rawReferences.some((reference) => reference === '')) return { kind: 'emptyReference' };

  if (context.references) {
    const selectableNames: string[] = [];
    for (const reference of context.references) {
      if (reference.isSelectable) selectableNames.push(reference.name);
    }

    for (const rawReference of rawReferences) {
      const reference = resolveReference(rawReference, context.references);
      if (!reference) {
        return {
          kind: 'unknownField',
          name: rawReference,
          suggestion: /^\d+$/.test(rawReference) ? undefined : closestFieldName(rawReference, selectableNames),
        };
      }
      if (!reference.isNumeric) return { kind: 'nonNumericField', name: reference.name };
      if (!reference.isSelectable && !context.grandfatheredIds?.has(reference.id)) {
        return { kind: 'outOfScopeField', name: reference.name };
      }
    }
  }

  const structural = tokenizeExpression(text).filter((token) => !(token.type === 'text' && token.text.trim() === ''));
  const isOperand = (token: Token): boolean =>
    token.type === 'field' || token.type === 'unknownField' || token.type === 'number';

  for (let index = 0; index + 1 < structural.length; index++) {
    const left = structural[index];
    const right = structural[index + 1];
    const missing =
      (isOperand(left) && isOperand(right)) ||
      (isOperand(left) && right.type === 'paren' && right.text === '(') ||
      (left.type === 'paren' && left.text === ')' && isOperand(right)) ||
      (left.type === 'paren' && left.text === ')' && right.type === 'paren' && right.text === '(');
    if (missing) return { kind: 'missingOperator', left: left.text, right: right.text };
  }

  const math = text.replace(SUBTASK_RE, '1').replace(REF_RE, '1');
  if (math.trim() === '') return undefined;
  if (/[^0-9+\-*/().\s]/.test(math)) return { kind: 'syntax' };
  if (Number.isNaN(expression_eval(math))) return { kind: 'syntax' };

  return undefined;
};

export const workedExample = (text: string): string | undefined => {
  const samples = new Map<string, number>();
  let nextSample = 10;
  const sampleFor = (key: string): number => {
    if (!samples.has(key)) {
      samples.set(key, nextSample);
      nextSample += 10;
    }

    return samples.get(key) as number;
  };

  const substituted = text
    .replace(SUBTASK_RE, (match) => String(sampleFor(match)))
    .replace(REF_RE, (_, name) => String(sampleFor(String(name).trim())));
  if (substituted.trim() === '' || /[^0-9+\-*/().\s]/.test(substituted)) return undefined;

  const result = expression_eval(substituted);
  if (Number.isNaN(result)) return undefined;

  return `${substituted.replace(/\s+/g, ' ').trim()} = ${(Math.round(result * 100) / 100).toLocaleString()}`;
};
